import { useEffect, useRef, useState } from "react";
import { ChevronDown, Copy, Pencil, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copyText } from "@/pages/landing/Checkout";

export const PH_RE = /\[([^\]\n]{1,40})\]/g;
export const fillPh = (text, vals) => (text || "").replace(PH_RE, (m, k) => (vals[k] ? vals[k] : m));

export async function copy(text) {
  if (await copyText(text)) toast.success("Copied!"); else toast.error("Could not copy. Please copy it by hand.");
}

export function Section({ id, title, subtitle, open, onToggle, children, badge }) {
  return (
    <section className="rounded-3xl bg-white border border-[#E1ECEF] overflow-hidden" data-testid={`toolkit-section-${id}`}>
      <button onClick={onToggle} aria-expanded={open} className="w-full text-left p-5 flex items-start justify-between gap-3 min-h-[64px]" data-testid={`toolkit-toggle-${id}`}>
        <span><span className="block font-display font-extrabold text-lg text-[#102B3F]">{title}</span><span className="block text-sm text-[#5C6B73] mt-0.5">{subtitle}</span>{badge}</span>
        <ChevronDown className={`h-6 w-6 shrink-0 text-[#5C6B73] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 space-y-3">{children}</div>}
    </section>
  );
}

export function MessageCard({ number, dkey, title, text, vals, draft, whatsapp, label = "Copy Message" }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(draft ?? "");
  const t = useRef(null);
  const shown = edit || val ? (val || fillPh(text, vals)) : fillPh(text, vals);
  const change = (v) => { setVal(v); clearTimeout(t.current); t.current = setTimeout(() => api.put(`/ideas/${number}/toolkit/draft`, { key: dkey, text: v }).catch(() => {}), 800); };
  if (!text) return null;
  return (
    <div className="rounded-2xl border border-[#E1ECEF]" data-testid={`toolkit-card-${dkey}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full text-left px-4 py-3 min-h-[52px] flex items-center justify-between gap-2 font-bold text-[#102B3F]" data-testid={`toolkit-card-toggle-${dkey}`}>{title}<ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {edit ? <textarea value={shown} onChange={(e) => change(e.target.value)} rows={7} className="w-full rounded-xl border border-[#0BAF9A] p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#0BAF9A]" data-testid={`toolkit-edit-${dkey}`} />
            : <p className="rounded-xl bg-[#F4F8F9] p-3 text-[15px] text-[#102B3F] whitespace-pre-wrap" data-testid={`toolkit-text-${dkey}`}>{shown}</p>}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => copy(shown)} className="min-h-[44px] px-4 rounded-xl bg-[#102B3F] text-white text-sm font-bold inline-flex items-center gap-2" data-testid={`toolkit-copy-${dkey}`}><Copy className="h-4 w-4" />{label}</button>
            <button onClick={() => { if (!edit && !val) setVal(fillPh(text, vals)); setEdit(!edit); }} className="min-h-[44px] px-4 rounded-xl border border-[#D5E3E8] text-sm font-bold inline-flex items-center gap-2" data-testid={`toolkit-editbtn-${dkey}`}>{edit ? <><Check className="h-4 w-4" />Done</> : <><Pencil className="h-4 w-4" />Edit / Personalise</>}</button>
            {whatsapp && <a href={`https://wa.me/?text=${encodeURIComponent(shown)}`} target="_blank" rel="noreferrer" className="min-h-[44px] px-4 rounded-xl bg-[#25D366] text-white text-sm font-bold inline-flex items-center gap-2" data-testid={`toolkit-share-${dkey}`}><MessageCircle className="h-4 w-4" />Share via WhatsApp</a>}
          </div>
          {edit && <p className="text-xs text-[#7D8C96]">Your edited version is saved to your account.</p>}
        </div>
      )}
    </div>
  );
}

export function useToolkit(number) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get(`/ideas/${number}/toolkit`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.status || 500));
  useEffect(() => { load(); }, [number]); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, setD, err, load];
}
